import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { getIngredientByName } from '../services/ingredientService';
import { Ingredient, Paper, TagSummary } from '../types/ingredient';
import { PaperModal } from '../components/PaperModal';
import { Header } from '../components/Header';
import './IngredientPage.css';

export const IngredientPage = () => {
  const { ingredientName } = useParams<{ ingredientName: string }>();
  const navigate = useNavigate();
  const [ingredient, setIngredient] = useState<Ingredient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaper, setSelectedPaper] = useState<{ paper: Paper; tagSummary: TagSummary | undefined } | null>(null);

  useEffect(() => {
    const fetchIngredient = async () => {
      if (!ingredientName) {
        navigate('/404');
        return;
      }

      try {
        setLoading(true);
        const data = await getIngredientByName(ingredientName);
        
        if (data) {
          setIngredient(data);
        } else {
          navigate('/404');
        }
      } catch (err) {
        setError('Failed to load ingredient data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchIngredient();
  }, [ingredientName, navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="ingredient-page loading">
          <div className="spinner"></div>
          <p>Loading ingredient information...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="ingredient-page error">
          <h1>Error</h1>
          <p>{error}</p>
        </div>
      </>
    );
  }

  if (!ingredient) {
    return null;
  }

  const pageTitle = `${ingredient.name} - POM Data`;
  const pageDescription = ingredient.description?.content 
    ? ingredient.description.content.substring(0, 155) 
    : `Information about ${ingredient.name}`;
  const pageUrl = `${window.location.origin}/${ingredient.id}`;

  // Helper function to get papers for a specific tag
  const getPapersForTag = (tagName: string) => {
    if (!ingredient.papers) return [];
    
    return ingredient.papers.filter(paper => 
      paper.tagSummaries?.some(summary => summary.tag === tagName)
    );
  };

  // Helper function to get tag summary for a paper and tag
  const getTagSummaryForPaper = (paper: Paper, tagName: string) => {
    return paper.tagSummaries?.find(summary => summary.tag === tagName);
  };

  const handlePaperClick = (paper: Paper, tagName: string) => {
    const tagSummary = getTagSummaryForPaper(paper, tagName);
    setSelectedPaper({ paper, tagSummary });
  };

  const handleCloseModal = () => {
    setSelectedPaper(null);
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": ingredient.name,
            "description": ingredient.description?.content || '',
            "dateModified": new Date().toISOString(),
            "author": {
              "@type": "Organization",
              "name": "POM Data"
            }
          })}
        </script>
      </Helmet>

      <Header />

      <div className="ingredient-page">
        <header className="ingredient-header">
          <h1>{ingredient.name}</h1>
        </header>

        <main className="ingredient-content">
          <section className="ingredient-section">
            <h2>Description</h2>
            <p>{ingredient.description?.content || 'No description available.'}</p>
          </section>

          {ingredient.tags && ingredient.tags.length > 0 && (
            <section className="ingredient-section">
              <h2>Risk Classifications</h2>
              <div className="tags-container">
                {ingredient.tags.map((tagItem, index) => {
                  const papersForTag = getPapersForTag(tagItem.tag);
                  
                  return (
                    <div key={index} className="tag-item">
                      <h3 className="tag-badge">{tagItem.tag}</h3>
                      <p className="tag-reason">{tagItem.reason}</p>
                      
                      {papersForTag.length > 0 && (
                        <div className="tag-papers">
                          <h4 className="papers-heading">Related Studies ({papersForTag.length})</h4>
                          <div className="papers-grid">
                            {papersForTag.map((paper, paperIndex) => (
                              <div 
                                key={paper.id || paperIndex} 
                                className="paper-card"
                                onClick={() => handlePaperClick(paper, tagItem.tag)}
                              >
                                <h5 className="paper-title">{paper.title}</h5>
                                {paper.authors && paper.authors.length > 0 && (
                                  <p className="paper-authors">
                                    {paper.authors.slice(0, 3).join(', ')}
                                    {paper.authors.length > 3 && '...'}
                                  </p>
                                )}
                                <span className="paper-click-hint">
                                  Click to view summary <ArrowRight size={14} className="hint-arrow" />
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </main>
        
        {/* Paper Modal */}
        {selectedPaper && (
          <PaperModal
            paper={selectedPaper.paper}
            tagSummary={selectedPaper.tagSummary}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </>
  );
};
